import { DocumentType } from 'types/DocumentType';
import { DynamicPropertyEvent } from 'types/DynamicPropertyEvent';
import { DynamicPropertyType } from 'types/DynamicPropertyType';
import { fill } from 'utils/LabelTemplate';

import type Ad from '../../Ad';
import type DynamicPropertiesManager from '../Manager';
import { getRoot } from '../tree';
import type DynamicGroup from './Group';
import { DynamicProperty } from './Property';

function adToStringRecord(ad: Ad) {
  const result: Record<string, string> = {};

  for (const [key, value] of Object.entries(ad.data)) {
    switch (typeof value) {
      case 'number':
      case 'boolean':
      case 'string':
        result[key] = String(value);
    }
  }

  // Careful: `index` is exposed raw, so {{ad.index}} is the 0-based sort
  // key - one less than the number the ad list shows the user, which is
  // `index + 1`. AdHub 2.1 avoided the trap by exposing only the 1-based
  // form, spelled `id`.
  //
  // `key` is 2.1's name for the document id (its CSV export emitted
  // `adID: self.parent.key`), kept as an alias so older templates resolve.
  result.id = ad.id;
  result.key = ad.id;

  return { ad: result };
}

export default class Label extends DynamicProperty<string> {
  type = DynamicPropertyType.LABEL;
  value = '';
  rendered = '';

  constructor(parent: DynamicGroup, name: string, value: string) {
    super(parent, name, value);
    // this.becomeObservable();
    const root = getRoot(this);
    root.addEventListener(DynamicPropertyEvent.CHANGED, this.render);

    // Note the constructor deliberately does not call `this.render()` here.
    // By this point `this.value` is '' from the field initializer, which
    // runs after super(), so we know the product of any .render() call will
    // also be empty string, which is already the case.
  }

  getString = () => {
    return this.value;
  };

  setDefault = () => {
    this.setInitialValue('Untitled');
  };

  getPreviewData = () => {
    return this.getString();
  };

  render = () => {
    const placeholderValues = this.prepareValuesForLabel();
    const fillResult = fill(this.value, placeholderValues);
    for (const diagMessage of fillResult.messages) {
      // TODO: is there some cheap way of rendering max once?
      //  like maybe key against the JSON.stringify of the last call's
      //  {this.value, placeholderValues} ?
      console.warn(`[Label.render] ${diagMessage}`);
    }
    if (!fillResult.text) {
      console.warn(`[Label.render] fill returned empty string`);
    }
    this.rendered = fillResult.text;
  };

  private prepareValuesForLabel(): any {
    // Values come from the property set this label *belongs to*. This used to
    // read `store.creative.ads.current` - the ad the user happens to have
    // selected - so any label that re-rendered while a different ad was
    // current was filled from that other ad's data. It looked fine while
    // editing a single ad (owner and current are the same ad then), and went
    // wrong after bulk operations: "Save Selected" writes N ads, N Firestore
    // snapshots return, every Ad.onUpdated dispatches CHANGED, and all N
    // labels re-render against whatever is current.
    const root = getRoot(this);
    const values = root.getPreviewData();

    // Only a DynamicPropertiesManager carries `parentDocument`, and only an Ad
    // owner contributes the {{ad.*}} namespace - a label hanging off a
    // Creative or Template just gets its own group's values.
    const owner = (root as Partial<DynamicPropertiesManager>).parentDocument;
    if (owner?.type === DocumentType.AD) {
      return { ...values, ...adToStringRecord(owner as Ad) };
    }

    return values;
  }

  setUsingCSV = (value: string) => {
    this.set(value);
  };
}
